import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, Wand2, X } from 'lucide-react';
import { ApiError, api } from '../lib/api.js';
import { Button, useToast } from '../ui/index.js';
import { MarkdownBody } from './MarkdownBody.js';

interface Suggested {
  reply: string;
  why: string;
  /** What becomes of it is recorded against this (082). */
  suggestionId: string | null;
  basis: { examples: number; sameType: number; articles: string[] };
}

/**
 * A suggested reply in the agent's own style (services/suggestReply.ts).
 * Shown above the composer for the agent to use, change or dismiss; the
 * replies they actually send are what the next suggestion learns from.
 */
export function useSuggestReply(tenant: string, number: string | number) {
  const toast = useToast();
  const [result, setResult] = useState<Suggested | null>(null);
  const run = useMutation({
    mutationFn: () => api.post<Suggested>(`/api/v1/tenants/${tenant}/tickets/${number}/suggest-reply`, {}),
    onSuccess: (r) => setResult(r),
    onError: (e) => toast.error('No suggestion', e instanceof ApiError ? e.message : undefined),
  });
  /**
   * Record that the agent used or dismissed it. Fire and forget: a record of
   * use that failed is not worth interrupting the agent for.
   */
  const mark = (action: 'used' | 'dismissed') => {
    const id = result?.suggestionId;
    if (!id) return;
    void api.post(`/api/v1/tenants/${tenant}/tickets/${number}/suggest-reply/${id}`, { action }).catch(() => undefined);
  };
  return { run, result, mark, clear: () => setResult(null) };
}

export function SuggestReplyButton({ onClick, pending }: { onClick: () => void; pending: boolean }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      leading={<Wand2 className="h-3.5 w-3.5" />}
      loading={pending}
      onClick={onClick}
      title="Draft a reply the way you usually write, from this ticket and the knowledgebase"
    >
      Suggest a reply
    </Button>
  );
}

function basisLine(b: Suggested['basis']): string {
  const style =
    b.examples === 0
      ? 'No replies of yours to learn from yet, so it is in a plain style.'
      : `In your style, from ${b.examples} of your recent repl${b.examples === 1 ? 'y' : 'ies'}${b.sameType ? ` (${b.sameType} on this kind of request)` : ''}.`;
  const kb = b.articles.length ? ` Drawing on ${b.articles.map((a) => `“${a}”`).join(', ')}.` : '';
  return style + kb;
}

export function SuggestReplyResult({
  result,
  hasDraft,
  onUse,
  onDismiss,
}: {
  result: Suggested;
  /** The agent has already written something: using it replaces that. */
  hasDraft: boolean;
  onUse: (markdown: string) => void;
  onDismiss: () => void;
}) {
  return (
    <div className="mt-2.5 rounded-md border border-brand/30 bg-brand-tint/40 p-2.5">
      <div className="mb-1.5 flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-brand">
        <Wand2 aria-hidden className="h-3 w-3" />
        Suggested reply
      </div>
      <MarkdownBody text={result.reply} className="max-h-64 overflow-y-auto rounded-sm bg-surface px-2.5 py-2" />
      <p className="mt-1.5 text-2xs leading-relaxed text-ink-tertiary">
        {basisLine(result.basis)} Check it before sending: it is a suggestion, not a fact-checked answer.
      </p>
      <div className="mt-2 flex items-center justify-end gap-1.5">
        <Button variant="ghost" size="sm" leading={<X className="h-3.5 w-3.5" />} onClick={onDismiss}>
          Dismiss
        </Button>
        <Button size="sm" leading={<Check className="h-3.5 w-3.5" />} onClick={() => onUse(result.reply)}>
          {hasDraft ? 'Replace my draft' : 'Use it'}
        </Button>
      </div>
    </div>
  );
}
